import React, { memo, useEffect, useMemo, useState } from 'react';
import { FeatureColumn } from './FeatureColumns';
import { HelpModal } from './HelpModal';
import { VersionFilterPanel } from './VersionFilterPanel';

// Helper function to group issues by their associated epic
const groupIssuesByEpic = (issues) => {
  return issues.reduce((acc, issue) => {
    if (!issue.fields?.issuelinks) return acc;

    const epicLink = issue.fields.issuelinks.find(
      (link) => link.outwardIssue?.fields.issuetype.name === 'Epic' || 
          link.outwardIssue?.fields.issuetype.name === 'Portfolio Epic'
    );
    const epicName = epicLink?.outwardIssue?.fields.summary ?? 'Unassigned';

    if (!acc[epicName]) {
      acc[epicName] = { name: epicName, issues: [] };
    }

    acc[epicName].issues.push(issue);
    return acc;
  }, {});
};

export const Featuremap = memo(({ issues, versions, expandState }) => {
  const [accordionState, setAccordionState] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([]);

  const unassignedVersion = 'Unassigned';

  const toggleMenu = () => setIsOpen((prev) => !prev);
  const hasIssues = issues?.length > 0;

  // Compute grouped epics from issues
  const epicsArray = useMemo(() => {
    return hasIssues ? Object.values(groupIssuesByEpic(issues)) : [];
  }, [hasIssues, issues]);

  // Initialize accordions open state when issues and versions are loaded
  useEffect(() => {
    if (!hasIssues || Object.keys(accordionState).length > 0) return;

    const initialState = {};

    versions.forEach((_, sortedIndex) => {
      epicsArray.forEach((_, epicIndex) => {
        const key = `${sortedIndex}-${epicIndex}`;
        initialState[key] = true;
      });
    });

    epicsArray.forEach((epic, epicIndex) => {
      const unassignedIssues = epic.issues.filter(
        (issue) => !issue.fields.versions || issue.fields.versions.length === 0
      );

      if (unassignedIssues.length > 0) {
        const key = `unassigned-${epicIndex}`;
        initialState[key] = true;
      }
    });

    setAccordionState(initialState);
  }, [hasIssues, epicsArray, versions, accordionState]);

  // Set all accordions to open or closed based on expandState
  useEffect(() => {
    if (!hasIssues || expandState === 'none') return;

    const newState = {};

    versions.forEach((_, sortedIndex) => {
      epicsArray.forEach((_, epicIndex) => {
        const key = `${sortedIndex}-${epicIndex}`;
        newState[key] = expandState === 'expanded';
      });
    });

    epicsArray.forEach((epic, epicIndex) => {
      const key = `unassigned-${epicIndex}`;
      newState[key] = expandState === 'expanded';
    });

    setAccordionState(newState);

    // Reset expand state to neutral after applying
  }, [expandState, versions, epicsArray, hasIssues]);

  // Initialize all versions as selected
  useEffect(() => {
    if (versions.length > 0) {
      const initialSelection = {
        Unassigned: true,
        ...Object.fromEntries(versions.map((v) => [v, true])),
      };

      setSelectedVersions(initialSelection);
    }
  }, [versions]);

  const handleToggle = (key) => {
    setAccordionState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    // Reset global expand state on manual interaction
  };

  const toggleVersions = (version) => {
    setSelectedVersions((prev) => ({
      ...prev,
      [version]: !prev[version],
    }));
  };

  if (!hasIssues) {
    return <HelpModal />;
  }

  return (
    <div className="kanban">
      <div className="overflow-x-auto">
        <div className="flex flex-col md:flex-row gap-4 min-w-full">
          {/* Unassigned Column */}
          {selectedVersions[unassignedVersion] && (
            <FeatureColumn
              title="Unassigned"
              epics={epicsArray}
              accordionState={accordionState}
              onToggle={handleToggle}
              visible={selectedVersions[unassignedVersion]}
              versionName="Unassigned"
              columnKeyPrefix="unassigned"
            />
          )}

          {/* Epic Columns by Version */}
          {versions.map((version, sortedIndex) => (
            <FeatureColumn
              key={version}
              title={version}
              versionName={version}
              epics={epicsArray}
              accordionState={accordionState}
              onToggle={handleToggle}
              visible={selectedVersions[version]}
              columnKeyPrefix={sortedIndex.toString()} // 👈 wichtig
            />
          ))}
        </div>
      </div>

      {/* Filter Panel with version checkboxes */}
      <VersionFilterPanel
        versions={versions}
        selectedVersions={selectedVersions}
        toggleVersions={toggleVersions}
        isOpen={isOpen}
        toggleMenu={toggleMenu}
      />
    </div>
  );
});
